import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSelectModule } from '@angular/material/select';
import { CommunicationService } from 'src/app/leads/leads-managment/signal-lead.serice';
import { CallDispositionsService } from 'src/app/services/call-disposition';
import { ActivityLeadService } from 'src/app/services/lead-activity.servise';
import { HtmlEditorComponent } from 'src/app/shared/html-editor/html-editor.component';
import { DialogComponent } from 'src/app/shared/dialog/confirm.dialog';
import { environment } from 'src/environments/environment';
import { CallSession } from 'src/types/telnyx-webrtc';
import { noteService } from 'src/app/services/note-service';
import { firstValueFrom } from 'rxjs';
import { UsersCacheService } from 'src/app/services/cache/users-cache';
import { activityTypeCache } from 'src/app/services/cache/activity-types-cache';
import { ActivityModalDataService } from 'src/app/services/lead-activity-modal.service';
import { CallDispositionModalComponent } from '../../settings/call-disposition-modal/call-disposition-modal.component';



@Component({
  selector: 'app-post-call-action-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    HtmlEditorComponent
  ],
  template: `
    <div class="post-call-modal">
      <!-- Header - Material Design Dialog Title -->
      <div class="modal-header">
        <h2 mat-dialog-title>Call Completed</h2>
        <button
          mat-icon-button
          class="close-btn"
          (click)="onCancel()"
          [disabled]="isLoading"
          aria-label="Close dialog">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>
        <!-- Call Summary Card - Only show before disposition is saved -->
        <div class="call-summary-card" *ngIf="!dispositionSaved" role="region" aria-label="Call summary">
          <div class="summary-row">
            <div class="summary-label">
              <mat-icon aria-hidden="true">phone</mat-icon>
              <span>Phone Number</span>
            </div>
            <div class="summary-value">{{formatPhoneNumber(data.phoneNumber)}}</div>
          </div>
          
        </div>

        <!-- Disposition Selection - Only show before disposition is saved -->
        <div class="form-section" *ngIf="!dispositionSaved">
          <label class="section-label" id="disposition-label">
            <mat-icon aria-hidden="true">assignment</mat-icon>
            Call Disposition <span class="required">*</span>
            <mat-icon
              *ngIf="hasSettingsAccess"
              class="settings-icon"
              matTooltip="Manage call dispositions"
              matTooltipPosition="above"
              (click)="openCallDispositionModal()">
              settings
            </mat-icon>
          </label>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Select call outcome</mat-label>
            <mat-select
              [(ngModel)]="selectedDisposition"
              (ngModelChange)="dispositionChanged()"
              aria-labelledby="disposition-label"
              required>
              <mat-option *ngFor="let disposition of dispositions" [value]="disposition.id">
                {{ disposition.name }}
              </mat-option>
            </mat-select>
            <mat-hint>Choose the outcome of this call</mat-hint>
          </mat-form-field>
        </div>

        <!-- Quick Actions -->
        <div class="form-section" *ngIf="dispositionSaved">
          <div class="action-grid" role="group" aria-label="Quick action buttons">
            <button
              mat-raised-button
              class="action-card"
              (click)="showNoteForm()"
              aria-label="Add a note about this call">
              <mat-icon aria-hidden="true">note_add</mat-icon>
              <span>Add Note</span>
            </button>

            <button
              mat-raised-button
              class="action-card"
              (click)="openActivityModal()"
              aria-label="Schedule a follow-up activity">
              <mat-icon aria-hidden="true">event</mat-icon>
              <span>Schedule Follow-up</span>
            </button>
          </div>
        </div>

        <!-- Note Form (Expandable) -->
        <div class="form-section note-section" *ngIf="showNote" role="form" aria-label="Call note form">
          <div class="note-header">
            <label class="section-label">
              <mat-icon aria-hidden="true">edit_note</mat-icon>
              Call Note
            </label>
            <button
              mat-icon-button
              (click)="cancelNote()"
              class="close-note-btn"
              aria-label="Close note form">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <app-html-editor
            [formControl]="noteContent"
            [height]="'200px'"
            [showNoteBackground]="true"
            placeholder="Add notes about this call..."
            [quillModule]="quillModule"
            aria-label="Note editor"
          ></app-html-editor>

          <div class="note-actions">
            <button mat-button (click)="cancelNote()">Cancel</button>
            <button
              mat-raised-button
              color="primary"
              [disabled]="!noteContent.value || savingNote"
              (click)="saveNote()"
              aria-label="Save note">
              <mat-spinner
                diameter="16"
                *ngIf="savingNote"
                class="inline-spinner"
                aria-label="Saving note"></mat-spinner>
              <span *ngIf="!savingNote">Save Note</span>
            </button>
          </div>
        </div>

        <!-- Success Message -->
        <div
          class="alert success-alert"
          *ngIf="successMessage"
          role="alert"
          aria-live="polite">
          <mat-icon aria-hidden="true">check_circle</mat-icon>
          <span>{{successMessage}}</span>
        </div>

        <!-- Error Message -->
        <div
          class="alert error-alert"
          *ngIf="errorMessage"
          role="alert"
          aria-live="assertive">
          <mat-icon aria-hidden="true">error</mat-icon>
          <span>{{errorMessage}}</span>
        </div>

        <!-- Loading State -->
        <div
          class="loading-overlay"
          *ngIf="isLoading"
          role="status"
          aria-live="polite"
          aria-label="Loading">
          <mat-spinner diameter="40" aria-label="Loading"></mat-spinner>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions>
        <mat-icon
          *ngIf="hasSettingsAccess"
          class="settings-icon"
          matTooltip="Manage call dispositions"
          matTooltipPosition="above"
          (click)="openCallDispositionModal()">
          settings
        </mat-icon>
        <div class="actions-spacer"></div>
        <button
          mat-button
          *ngIf="!dispositionSaved"
          (click)="onCancel()"
          [disabled]="isLoading"
          aria-label="Cancel and close dialog">
          Cancel
        </button>
        <button
          mat-raised-button
          color="primary"
          *ngIf="!dispositionSaved"
          [disabled]="!selectedDisposition || isLoading"
          (click)="onSaveDisposition()"
          aria-label="Save disposition">
          <mat-spinner
            diameter="16"
            *ngIf="isLoading"
            class="inline-spinner"
            aria-label="Saving"></mat-spinner>
          <span *ngIf="!isLoading">Save Disposition</span>
        </button>
        <button
          mat-raised-button
          color="primary"
          *ngIf="dispositionSaved"
          (click)="onClose()"
          aria-label="Close dialog">
          Close
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styleUrl: './post-call-action-modal.component.scss'
})
export class PostCallActionModalComponent implements OnInit {
  // View state
  showNote = false;

  // Form controls
  noteContent = new FormControl('');

  // State flags
  savingNote = false;
  isLoading = false;
  successMessage = '';
  errorMessage = '';
  dispositionSaved = false;
  private savingDisposition = false; // Prevent concurrent saves

  // Call dispositions
  dispositions: any[] = [];
  selectedDisposition: number | null = null;
  private initialDisposition: number | null = null;

  // Activity types and users for modal
  activityTypes: any[] = [];
  users: any[] = [];
  hasSettingsAccess: boolean = false;

  // Editor config
  quillModule = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        ['link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean']
      ]
    }
  };

  constructor(
    public dialogRef: MatDialogRef<PostCallActionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      call: CallSession,
      leadId: number,
      phoneNumber: string,
      duration: number,
      callId: string
    },
    private router: Router,
    private activityLeadService: ActivityLeadService,
    private communicationService: CommunicationService,
    private noteService: noteService,
    private callDispositionsService: CallDispositionsService,
    private dialog: MatDialog,
    private usersCacheService: UsersCacheService,
    private activityTypeCacheService: activityTypeCache,
    private activityModalDataService: ActivityModalDataService, // ADD THIS

  ) {
    // Prevent closing on backdrop click
    this.dialogRef.disableClose = true;
    const user = localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')) : null;
    this.hasSettingsAccess = user?.role === 'ADMIN';
  }

  async ngOnInit(): Promise<void> {
    this.loadCallDispositions();
    await this.loadActivityTypesAndUsers();

    // Initialize with default note content
    const callDetails = `Call with ${this.formatPhoneNumber(this.data.phoneNumber)} (${this.formatDuration(this.data.duration)})`;
    this.noteContent.setValue(callDetails);
  }

  async loadActivityTypesAndUsers(): Promise<void> {
    try {
      this.activityTypeCacheService.fetchData();
      this.usersCacheService.fetchData();

      const [activityTypeCache, usersCache] = await Promise.all([
        firstValueFrom(this.activityTypeCacheService.data$),
        firstValueFrom(this.usersCacheService.data$),
      ]);

      this.activityTypes = activityTypeCache;
      this.users = usersCache;
    } catch (error) {
      console.error('Error loading activity types and users:', error);
    }
  }

  loadCallDispositions(): void {
    this.callDispositionsService.getCallDispositions().subscribe(
      (response: any) => {
        if (response && response.data) {
          this.dispositions = response.data.filter((d: any) => d.active);

          // Leave disposition empty by default (no auto-selection)
          this.selectedDisposition = null;
          this.initialDisposition = null;
        }
      },
      error => {
        console.error('Error loading call dispositions:', error);
      }
    );
  }

  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';
    return phoneNumber;
  }

  formatDuration(seconds: number): string {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  dispositionChanged(): void {
    // Clear any previous messages
    this.errorMessage = '';
    this.successMessage = '';

    // Disposition will be saved when user clicks "Done" button
  }

  showNoteForm(): void {
    this.showNote = true;
  }

  cancelNote(): void {
    this.showNote = false;
    // Reset note content to default
    const callDetails = `Call with ${this.formatPhoneNumber(this.data.phoneNumber)} (${this.formatDuration(this.data.duration)})`;
    this.noteContent.setValue(callDetails);
    this.successMessage = '';
  }

  openActivityModal(): void {
    const dialogRef = this.activityLeadService.openActivityOnlyModal({
      id: new Date(),
      hourSegments: 30,
      detailed: true,
      hideCalendarProps: false,
      isExternal: true,
      isModal: true,
      defaultLead: this.data.leadId,
      activityTypes: this.activityTypes,
      users: this.users,
      hideNavigation: true,
    });
  
  }

  async saveDisposition() {
    // Prevent concurrent saves
    if (this.savingDisposition) {
      return;
    }

    if (!this.data.callId || !this.selectedDisposition) {
      return;
    }

    this.savingDisposition = true;
    this.errorMessage = '';

    const data = {
      disposition_id: this.selectedDisposition
    }

    try {
      await firstValueFrom(this.callDispositionsService.postDisposition(this.data.callId, data));
      this.dispositionSaved = true;
      this.initialDisposition = this.selectedDisposition;
      this.successMessage = 'Disposition saved successfully';

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.successMessage = '';
      }, 3000);
    } catch (error: any) {
      console.error('Error saving disposition:', error);
      this.errorMessage = error?.error?.message || 'Failed to save disposition. Please try again.';
      this.dispositionSaved = false;

      // Clear error message after 5 seconds
      setTimeout(() => {
        this.errorMessage = '';
      }, 5000);
    } finally {
      this.savingDisposition = false;
    }
  }

// Update the saveNote method
async saveNote(): Promise<void> {
  if (!this.noteContent.value) return;

  this.savingNote = true;
  this.successMessage = '';

  try {
    const noteData = {
      content: this.noteContent.value,
      lead_id: this.data.leadId
    };

    const response = await this.noteService.post(noteData).toPromise();

    if (response) {
      this.successMessage = 'Note saved successfully';

      // Notify via communicationService (existing)
      this.communicationService.updateData({
        type: 'add-note',
        obj: {
          data: response.data,
          location: 'post-call-modal',
          update: true,
          newNote: true
        }
      });

      // Also emit via activityModalDataService for inbox components
      this.activityModalDataService.emitEvent('create', {
        id: response.data?.id,
        lead_id: this.data.leadId,
        type: 'note',
        ...response.data
      } as any);

      // Hide note form after short delay
      setTimeout(() => {
        this.showNote = false;
        this.successMessage = '';
      }, 2000);
    }
  } catch (error) {
    console.error('Error saving note:', error);
    this.successMessage = 'Failed to save note';
  } finally {
    this.savingNote = false;
  }
}

  hasUnsavedChanges(): boolean {
    // Check if disposition selected but not saved
    if (this.selectedDisposition && !this.dispositionSaved) {
      return true;
    }

    // Check if note content was modified from default
    const defaultNote = `Call with ${this.formatPhoneNumber(this.data.phoneNumber)} (${this.formatDuration(this.data.duration)})`;
    if (this.showNote && this.noteContent.value !== defaultNote) {
      return true;
    }

    return false;
  }

  onCancel(): void {
    if (this.hasUnsavedChanges()) {
      // Show confirmation dialog if there are unsaved changes
      const dialogData = {
        header: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to close?',
        type: 'warning',
        buttonName: 'Discard & Close'
      };

      const confirmDialogRef = this.dialog.open(DialogComponent, {
        width: '400px',
        data: dialogData
      });

      confirmDialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.dialogRef.close();
        }
      });
    } else if (!this.dispositionSaved) {
      // Show warning if no disposition has been saved yet
      const dialogData = {
        header: 'Disposition Required',
        content: 'You must save a disposition before closing. Are you sure you want to close without saving a disposition?',
        type: 'warning',
        buttonName: 'Close Anyway'
      };

      const confirmDialogRef = this.dialog.open(DialogComponent, {
        width: '400px',
        data: dialogData
      });

      confirmDialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  async onSaveDisposition(): Promise<void> {
    if (!this.selectedDisposition) {
      return;
    }

    // Save disposition but keep modal open
    this.isLoading = true;
    this.errorMessage = '';

    try {
      await this.saveDisposition();
      // Modal stays open to allow adding notes or activities
    } catch (error) {
      console.error('Error saving disposition:', error);
      this.errorMessage = 'Failed to save disposition. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  onClose(): void {
    // Close modal after disposition has been saved
    this.dialogRef.close({
      disposition_id: this.selectedDisposition,
      completed: true
    });
  }

  openCallDispositionModal(): void {
    const dialogRef = this.dialog.open(CallDispositionModalComponent, {
      width: '800px'
    });
  
    dialogRef.afterClosed().subscribe(result => {
      // Reload dispositions so any newly created ones appear in the dropdown
      this.loadCallDispositions();
    });
  }
}